/**
 * The realtime client: a thin typed wrapper over `partysocket` (auto-reconnect)
 * that feeds every Server→Client message into the Zustand store and exposes a
 * `send(intent)` that only ever emits frozen `ClientIntent` shapes.
 *
 * The client holds NO authoritative state (CLAUDE.md rule 1): on reconnect we
 * simply re-sync from the next authoritative broadcast. A client-initiated
 * `ping` heartbeat keeps the socket warm (and satisfies the Render fallback's
 * idle timer — ARCHITECTURE.md §4).
 */

import { PartySocket } from 'partysocket';
import type { ClientIntent, ServerMessage } from '@blackjack/shared';
import { useGameStore } from '../state/store';

/** How often the client emits a heartbeat ping (ms). Incoming WS msgs bill 20:1. */
const HEARTBEAT_INTERVAL_MS = 20_000;

/**
 * Resolve the realtime host. In prod the Worker is routed under the same custom
 * domain (ARCHITECTURE.md §6); in dev it defaults to wrangler's localhost. Both
 * are overridable via `VITE_PARTY_HOST`.
 */
function resolveHost(): string {
  const fromEnv = import.meta.env.VITE_PARTY_HOST as string | undefined;
  if (fromEnv && fromEnv.length > 0) return fromEnv;
  if (import.meta.env.DEV) return 'localhost:8787';
  return window.location.host;
}

/** A single room id for this game (one DO instance == one room). */
const ROOM_ID = 'standup';

export interface RealtimeClient {
  /** Send a typed intent to the server. No-op if the socket isn't open. */
  send(intent: ClientIntent): void;
  /** Tear down the socket + heartbeat. */
  close(): void;
}

/**
 * Open the realtime connection and start wiring messages into the store. Returns
 * a {@link RealtimeClient}. Idempotent per call — callers should hold one.
 */
export function createRealtimeClient(): RealtimeClient {
  const store = useGameStore.getState();
  store.setConnection('connecting');

  const socket = new PartySocket({
    host: resolveHost(),
    // PartyServer routes the DO under a kebab-cased party name derived from the
    // binding/class name. The server registers the `BlackjackRoom` DO (see
    // packages/server/wrangler.jsonc + src/index.ts), which PartyServer's
    // camelCaseToKebabCase() exposes as the party `blackjack-room`. The default
    // `main` does not exist and the server 400s with exactly this hint, so we
    // pin the party to the server's authoritative name.
    party: 'blackjack-room',
    room: ROOM_ID,
    // partysocket auto-reconnects; let it manage backoff.
  });

  let heartbeat: ReturnType<typeof setInterval> | undefined;

  const startHeartbeat = () => {
    stopHeartbeat();
    heartbeat = setInterval(() => {
      // Client-initiated heartbeat (ARCHITECTURE.md §4 / CLAUDE.md rule 3).
      send({ type: 'ping' });
    }, HEARTBEAT_INTERVAL_MS);
  };
  const stopHeartbeat = () => {
    if (heartbeat !== undefined) {
      clearInterval(heartbeat);
      heartbeat = undefined;
    }
  };

  socket.addEventListener('open', () => {
    useGameStore.getState().setConnection('open');
    startHeartbeat();
    // Re-announce identity on (re)connect so the server re-attaches us to the
    // roster after a hibernation wake / reconnect. The server is the source of
    // truth and will re-broadcast state.
    const id = useGameStore.getState().identity;
    if (id) {
      send({ type: 'join', name: id.name, role: id.role, clientId: id.clientId });
    }
  });

  socket.addEventListener('message', (event: MessageEvent) => {
    const parsed = parseServerMessage(event.data);
    if (parsed) useGameStore.getState().ingest(parsed);
  });

  socket.addEventListener('close', () => {
    stopHeartbeat();
    useGameStore.getState().setConnection('closed');
  });

  socket.addEventListener('error', () => {
    useGameStore.getState().setConnection('error');
  });

  function send(intent: ClientIntent): void {
    if (socket.readyState === socket.OPEN) {
      socket.send(JSON.stringify(intent));
    }
  }

  return {
    send,
    close: () => {
      stopHeartbeat();
      socket.close();
    },
  };
}

/** Defensive parse of an incoming frame into a discriminated `ServerMessage`. */
function parseServerMessage(data: unknown): ServerMessage | undefined {
  if (typeof data !== 'string') return undefined;
  let obj: unknown;
  try {
    obj = JSON.parse(data);
  } catch {
    return undefined;
  }
  if (typeof obj !== 'object' || obj === null) return undefined;
  if (!('type' in obj) || typeof (obj as { type: unknown }).type !== 'string') return undefined;
  // Trust the discriminant; the server is authoritative and emits frozen shapes.
  return obj as ServerMessage;
}
